import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { useUserStats } from "../lib/UserStatsContext";
import { checkUsageLimit, recordFeatureUsage } from "../lib/userStats";
import { generateMealPlan, regenerateMeal as regenerateMealFn } from "../lib/gemini";
import UsageLimitModal from "./UsageLimitModal";
import { BackIcon } from "./Icons";

interface PlannedMeal {
  name: string;
  gi: number;
  giLevel: "low" | "medium" | "high";
  prepTime: string;
  ingredients: string[];
  tip?: string;
}

interface DayPlan {
  day: string;
  breakfast?: PlannedMeal;
  lunch?: PlannedMeal;
  dinner?: PlannedMeal;
  snack?: PlannedMeal;
}

interface MealPlanConstraints {
  prepTimeMinutes: number;
  targetGI: "low" | "any";
  servings: number;
  excludeIngredients: string;
}

interface MealPlannerProps {
  onBack?: () => void;
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const PREP_TIME_OPTIONS = [
  { value: 15, label: "Quick (15 min)" },
  { value: 30, label: "Medium (30 min)" },
  { value: 45, label: "Flexible (45+ min)" },
];

export default function MealPlanner({ onBack }: MealPlannerProps) {
  const { user } = useAuth();
  const { dietaryPrefs } = useUserStats();
  const [constraints, setConstraints] = useState<MealPlanConstraints>({
    prepTimeMinutes: 30,
    targetGI: "low",
    servings: 2,
    excludeIngredients: "",
  });
  const [mealPlan, setMealPlan] = useState<DayPlan[] | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedDay, setSelectedDay] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [shoppingList, setShoppingList] = useState<string | null>(null);
  const [showShoppingList, setShowShoppingList] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState<{ currentUsage: number; isPremiumOnly: boolean }>({ currentUsage: 0, isPremiumOnly: false });

  const handleGenerateMealPlan = async () => {
    if (!user) return;

    const usageCheck = await checkUsageLimit(user.uid, "mealPlanner");
    if (!usageCheck.allowed) {
      setLimitInfo({ currentUsage: usageCheck.currentUsage, isPremiumOnly: usageCheck.isPremiumOnly });
      setShowLimitModal(true);
      return;
    }

    setIsGenerating(true);
    setError(null);
    setMealPlan(null);
    setShoppingList(null);

    try {
      const text = await generateMealPlan(constraints, dietaryPrefs);

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error("Invalid response format");
      }

      const parsed = JSON.parse(jsonMatch[0]);
      setMealPlan(parsed.days);
      if (parsed.shoppingList) {
        setShoppingList(parsed.shoppingList.join("\n"));
      }

      await recordFeatureUsage(user.uid, "mealPlanner");
    } catch (err) {
      console.error("Error generating meal plan:", err);
      setError("Failed to generate meal plan. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateMeal = async (dayIndex: number, mealType: "breakfast" | "lunch" | "dinner" | "snack") => {
    if (!mealPlan) return;

    const day = mealPlan[dayIndex];
    const currentMeal = day[mealType];

    try {
      const text = await regenerateMealFn(mealType, currentMeal?.name || "current meal", constraints, dietaryPrefs);
      const jsonMatch = text.match(/\{[\s\S]*?\}/);
      if (jsonMatch) {
        const newMeal = JSON.parse(jsonMatch[0]);
        const newPlan = [...mealPlan];
        newPlan[dayIndex] = { ...newPlan[dayIndex], [mealType]: newMeal };
        setMealPlan(newPlan);
      }
    } catch (err) {
      console.error("Error regenerating meal:", err);
    }
  };

  const currentDay = mealPlan?.[selectedDay];

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted font-[family-name:--font-ui] mb-4 hover:text-text">
          <BackIcon className="w-4 h-4" /> Back
        </button>
      )}
      <div className="section-label">Weekly Meal Planner</div>
      <p className="text-sm text-muted mb-6">AI-powered meal plans tailored to your needs</p>

      {/* Constraints Form */}
      {!mealPlan && (
        <div className="mb-6">
          <p className="text-sm text-muted font-[family-name:--font-ui] font-medium mb-4">Plan Your Week</p>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Prep Time */}
            <div>
              <label className="block text-sm text-muted font-[family-name:--font-ui] font-medium mb-2">
                Max Prep Time per Meal
              </label>
              <div className="flex flex-wrap gap-2">
                {PREP_TIME_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setConstraints({ ...constraints, prepTimeMinutes: opt.value })}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      constraints.prepTimeMinutes === opt.value
                        ? "bg-accent text-white"
                        : "border border-border text-text hover:bg-black/5"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Target GI */}
            <div>
              <label className="block text-sm text-muted font-[family-name:--font-ui] font-medium mb-2">
                Target Glycemic Index
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setConstraints({ ...constraints, targetGI: "low" })}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    constraints.targetGI === "low"
                      ? "gi-pill gi-pill--low !inline-flex !px-3 !py-2 !text-sm !rounded-md"
                      : "border border-border text-text hover:bg-black/5"
                  }`}
                >
                  Low GI Only
                </button>
                <button
                  onClick={() => setConstraints({ ...constraints, targetGI: "any" })}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    constraints.targetGI === "any"
                      ? "gi-pill gi-pill--medium !inline-flex !px-3 !py-2 !text-sm !rounded-md"
                      : "border border-border text-text hover:bg-black/5"
                  }`}
                >
                  Any GI
                </button>
              </div>
            </div>

            {/* Servings */}
            <div>
              <label className="block text-sm text-muted font-[family-name:--font-ui] font-medium mb-2">
                Servings (people)
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setConstraints({ ...constraints, servings: Math.max(1, constraints.servings - 1) })}
                  className="w-10 h-10 rounded-md border border-border text-text hover:bg-black/5 font-bold"
                >
                  -
                </button>
                <span className="text-xl font-semibold text-text w-8 text-center font-[family-name:--font-ui]">
                  {constraints.servings}
                </span>
                <button
                  onClick={() => setConstraints({ ...constraints, servings: Math.min(8, constraints.servings + 1) })}
                  className="w-10 h-10 rounded-md border border-border text-text hover:bg-black/5 font-bold"
                >
                  +
                </button>
              </div>
            </div>

            {/* Exclude Ingredients */}
            <div>
              <label className="block text-sm text-muted font-[family-name:--font-ui] font-medium mb-2">
                Exclude Ingredients (optional)
              </label>
              <input
                type="text"
                value={constraints.excludeIngredients}
                onChange={(e) => setConstraints({ ...constraints, excludeIngredients: e.target.value })}
                placeholder="e.g., mushrooms, cilantro"
                className="w-full px-3 py-2 border border-border rounded-md bg-bg text-text text-sm focus:border-accent outline-none"
              />
            </div>
          </div>

          {/* Diet Info */}
          {dietaryPrefs && (dietaryPrefs.dietType !== "none" || dietaryPrefs.allergies?.length > 0) && (
            <div className="mt-6 pt-4 border-t border-border">
              <p className="text-sm text-muted">
                <span className="font-medium">Your preferences:</span>{" "}
                {dietaryPrefs.dietType !== "none" && `${dietaryPrefs.dietType}`}
                {dietaryPrefs.allergies?.length > 0 && ` (avoiding: ${dietaryPrefs.allergies.join(", ")})`}
              </p>
            </div>
          )}

          {/* Generate Button */}
          <button
            onClick={handleGenerateMealPlan}
            disabled={isGenerating}
            className="w-full mt-6 py-3 rounded-md bg-accent text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isGenerating ? (
              <>
                <div className="spinner spinner--sm"></div>
                Generating Your Plan...
              </>
            ) : (
              "Generate Meal Plan"
            )}
          </button>

          {error && (
            <p className="mt-4 text-sm text-gi-high text-center">{error}</p>
          )}
        </div>
      )}

      {/* Meal Plan Display */}
      {mealPlan && (
        <div className="space-y-4">
          {/* Day Selector */}
          <div className="flex gap-4 overflow-x-auto pb-2 border-b border-border">
            {DAYS.map((day, i) => (
              <button
                key={day}
                onClick={() => setSelectedDay(i)}
                className={`flex-shrink-0 pb-2 text-sm font-[family-name:--font-ui] font-medium transition-colors ${
                  selectedDay === i
                    ? "text-accent border-b-2 border-accent"
                    : "text-muted hover:text-text"
                }`}
              >
                {day.slice(0, 3)}
              </button>
            ))}
          </div>

          {/* Current Day Meals */}
          {currentDay && (
            <div>
              <h3 className="font-semibold text-text mb-4 text-lg">{currentDay.day}</h3>

              <div>
                {(["breakfast", "lunch", "dinner", "snack"] as const).map((mealType) => {
                  const meal = currentDay[mealType];
                  if (!meal) return null;

                  return (
                    <div key={mealType} className="border-b border-border-light pb-4 mb-4 last:border-b-0 last:mb-0 last:pb-0">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-[11px] text-muted uppercase tracking-wider font-[family-name:--font-ui] font-semibold">
                            {mealType}
                          </p>
                          <h4 className="font-semibold text-text">{meal.name}</h4>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`gi-pill gi-pill--${meal.giLevel}`}>
                            GI {meal.gi}
                          </span>
                          <button
                            onClick={() => handleRegenerateMeal(selectedDay, mealType)}
                            className="p-1.5 rounded-md text-muted hover:text-accent transition-colors"
                            title="Get different suggestion"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 text-xs text-muted mb-2">
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {meal.prepTime}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1">
                        {meal.ingredients.map((ing, i) => (
                          <span key={i} className="px-2 py-0.5 border border-border rounded-md text-xs text-muted">
                            {ing}
                          </span>
                        ))}
                      </div>

                      {meal.tip && (
                        <p className="mt-2 text-xs text-muted">
                          {meal.tip}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowShoppingList(!showShoppingList)}
              className="flex-1 py-3 rounded-md border border-border text-text font-medium hover:bg-black/5 transition-colors"
            >
              {showShoppingList ? "Hide" : "View"} Shopping List
            </button>
            <button
              onClick={() => {
                setMealPlan(null);
                setShoppingList(null);
              }}
              className="flex-1 py-3 text-accent font-medium hover:underline transition-colors"
            >
              New Plan
            </button>
          </div>

          {/* Shopping List */}
          {showShoppingList && shoppingList && (
            <div className="border-t border-border pt-4">
              <h3 className="text-sm text-muted font-[family-name:--font-ui] font-medium mb-3">
                Shopping List
              </h3>
              <div className="space-y-1">
                {shoppingList.split("\n").map((item, i) => (
                  <label key={i} className="flex items-center gap-2 text-sm text-text cursor-pointer group">
                    <input type="checkbox" className="border border-border rounded-md text-accent focus:ring-accent" />
                    <span className="group-has-[:checked]:line-through group-has-[:checked]:text-muted">
                      {item}
                    </span>
                  </label>
                ))}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shoppingList);
                }}
                className="mt-4 w-full py-2 rounded-md border border-border text-muted text-sm font-medium hover:bg-black/5 transition-colors"
              >
                Copy to Clipboard
              </button>
            </div>
          )}
        </div>
      )}

      {/* Usage Limit Modal */}
      <UsageLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        feature="mealPlanner"
        currentUsage={limitInfo.currentUsage}
        isPremiumOnly={limitInfo.isPremiumOnly}
      />
    </div>
  );
}
